/* DoctorSol — Rustige Premium Homepage (Tesla + trusted advisor + Apple simplicity) */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ShieldCheck, Star, ChevronDown, Check, Sparkles,
  Mail, Loader2,
} from "lucide-react";
import { apiClient } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";

const IMG = {
  hero: "/img/hero-home.jpg",
  advisor: "/img/advisor-talk.jpg",
  couple: "/img/couple-tablet.jpg",
};

export default function Landing() {
  const { t, i18n } = useTranslation();
  const [regions, setRegions] = useState([]);
  const [referrerCode, setReferrerCode] = useState("");

  useEffect(() => {
    apiClient.get("/regions").then((r) => setRegions(r.data.regions)).catch(() => {});
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) {
        const cleaned = ref.trim().toUpperCase().slice(0, 16);
        localStorage.setItem("doctorsol_ref", cleaned);
        setReferrerCode(cleaned);
      } else {
        const stored = localStorage.getItem("doctorsol_ref");
        if (stored) setReferrerCode(stored);
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0B1B2B] antialiased">
      <Header />

      {/* 1. HERO ─────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B2B]/85 via-[#0B1B2B]/60 to-[#0B1B2B]/10" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-36 md:pt-44 md:pb-52 text-white">
          <div className="max-w-2xl">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-7">
              {t("ds.hero.title")}
            </h1>
            <p className="text-lg sm:text-xl text-white/85 leading-relaxed mb-10 max-w-xl">
              {t("ds.hero.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <a href="#zonnecheck" data-testid="hero-cta-primary"
                className="bg-[#F5C24C] hover:bg-[#E5AE2F] text-[#0B1B2B] font-medium rounded-full px-8 py-4 inline-flex items-center justify-center gap-2 transition-all shadow-lg shadow-black/10">
                {t("ds.hero.cta_primary")} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/hoe-werkt-het" data-testid="hero-cta-secondary"
                className="border border-white/30 hover:border-white/60 bg-white/5 backdrop-blur-sm text-white rounded-full px-8 py-4 inline-flex items-center justify-center gap-2 transition-all">
                {t("ds.hero.cta_secondary")}
              </Link>
            </div>
            <p className="text-sm text-white/55">{t("ds.hero.trustline")}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 text-sm text-white/75">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#A6E5C8]" strokeWidth={2.5} /> {t("ds.hero.usp1")}</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#A6E5C8]" strokeWidth={2.5} /> {t("ds.hero.usp2")}</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#A6E5C8]" strokeWidth={2.5} /> {t("ds.hero.usp3")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRUST BAR ────────────────────────────────── */}
      <section className="bg-white border-b border-[#F0F2F5] py-7">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center justify-center md:justify-between gap-x-8 gap-y-3 text-sm text-[#52606E]">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#1F7A4B]" />
                <span>{t(`ds.trustbar.${i}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. HOE WERKT HET — 3 stappen ────────────────── */}
      <section className="py-28 md:py-36 bg-[#F7F8FA]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.25em] text-[#1F3A60] font-medium mb-4">{t("ds.how.kicker")}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1B2B] mb-4">{t("ds.how.title")}</h2>
            <p className="text-[#52606E] text-lg">{t("ds.how.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-5">
            {[1, 2, 3].map((n) => (
              <div key={n} data-testid={`step-${n}`} className="text-center md:text-left">
                <div className="font-serif text-6xl text-[#F5C24C] mb-5 leading-none">{`0${n}`}</div>
                <h3 className="font-serif text-2xl text-[#0B1B2B] mb-3">{t(`ds.how.s${n}_title`)}</h3>
                <p className="text-[#52606E] leading-relaxed">{t(`ds.how.s${n}_desc`)}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/hoe-werkt-het" className="text-[#1F3A60] hover:text-[#0B1B2B] font-medium inline-flex items-center gap-2">
              {t("ds.how.more")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. VOOR WIE — 3 herkenning cards ───────────── */}
      <section className="py-28 md:py-36 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.25em] text-[#1F3A60] font-medium mb-4">{t("ds.for_whom.kicker")}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1B2B] mb-4">{t("ds.for_whom.title")}</h2>
            <p className="text-[#52606E] text-lg">{t("ds.for_whom.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} data-testid={`forwhom-${n}`} className="bg-[#F7F8FA] rounded-2xl p-8 hover:bg-[#FFF9EB] transition-colors">
                <p className="font-serif text-xl text-[#0B1B2B] mb-4 leading-snug">{`"${t(`ds.for_whom.c${n}_quote`)}"`}</p>
                <p className="text-[#52606E] leading-relaxed">{t(`ds.for_whom.c${n}_answer`)}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#52606E] mt-12 max-w-xl mx-auto italic">{t("ds.for_whom.footer_note")}</p>
        </div>
      </section>

      {/* 5. REVIEWS ──────────────────────────────────── */}
      <section className="py-28 md:py-36 bg-[#F7F8FA]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((s) => (<Star key={s} className="w-5 h-5 text-[#F5C24C] fill-[#F5C24C]" />))}
              <span className="ml-2 text-sm font-medium text-[#0B1B2B]">4,9 / 5 · 187 {t("ds.reviews.reviews")}</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0B1B2B]">{t("ds.reviews.title")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Ana & Pedro Ruiz", city: "Málaga", k: "t1" },
              { name: "Hans van der Berg", city: "Marbella", k: "t2" },
              { name: "Carmen López", city: "Mijas", k: "t3" },
            ].map((r, i) => (
              <div key={i} data-testid={`review-${i}`} className="bg-white rounded-2xl p-7 shadow-sm">
                <div className="flex gap-0.5 mb-4">
                  {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-4 h-4 text-[#F5C24C] fill-[#F5C24C]" />)}
                </div>
                <p className="text-[#0B1B2B] leading-relaxed mb-5">"{t(`ds.reviews.${r.k}`)}"</p>
                <div className="text-sm">
                  <div className="font-medium text-[#0B1B2B]">{r.name}</div>
                  <div className="text-[#52606E]">{r.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. EARNINGS / TRANSPARENT — single line ────── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#52606E] leading-relaxed text-lg">
            {t("ds.earning.line1")}
          </p>
          <p className="text-[#0B1B2B] mt-4 text-base italic">
            {t("ds.earning.line2")}
          </p>
        </div>
      </section>

      {/* 7. ZONNECHECK CTA + light form ──────────────── */}
      <SunCheckForm referrerCode={referrerCode} regions={regions} i18n={i18n} t={t} />

      {/* 8. MINI FAQ — max 3 ─────────────────────────── */}
      <section className="py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.25em] text-[#1F3A60] font-medium mb-4">{t("ds.faq.kicker")}</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0B1B2B]">{t("ds.faq.title")}</h2>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((idx) => <FaqItem key={idx} idx={idx} t={t} />)}
          </div>
          <div className="text-center mt-10">
            <Link to="/faq" className="text-[#1F3A60] hover:text-[#0B1B2B] font-medium inline-flex items-center gap-2">
              {t("ds.faq.more")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FaqItem({ idx, t }) {
  const [open, setOpen] = useState(idx === 1);
  return (
    <div data-testid={`faq-${idx}`} className={`border rounded-2xl transition-all ${open ? "border-[#F5C24C] bg-[#FFF9EB]" : "border-[#E5E9EE] bg-white"}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center text-left px-6 py-5">
        <span className="font-medium text-[#0B1B2B] pr-4">{t(`ds.faq.q${idx}`)}</span>
        <ChevronDown className={`w-5 h-5 text-[#52606E] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-5 text-[#52606E] leading-relaxed">{t(`ds.faq.a${idx}`)}</div>}
    </div>
  );
}

function SunCheckForm({ referrerCode, regions, i18n, t }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [region, setRegion] = useState("madrid");
  const [bill, setBill] = useState(120);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !whatsapp || !bill) { toast.error(t("ds.form.err_required")); return; }
    setBusy(true);
    try {
      const r = await apiClient.post("/leads", {
        name, email, whatsapp, region, monthly_bill_eur: Number(bill),
        language: i18n.language?.split("-")[0] || "es",
        referrer_code: referrerCode || null,
      });
      setDone(r.data);
      toast.success(t("ds.form.success"));
    } catch { toast.error(t("ds.form.err_send")); }
    finally { setBusy(false); }
  };

  return (
    <section id="zonnecheck" className="py-28 md:py-36 bg-gradient-to-br from-[#0B1B2B] to-[#1F3A60] text-white">
      <div className="max-w-5xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F5C24C] font-medium mb-4">{t("ds.form.kicker")}</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-5 leading-tight">{t("ds.form.title")}</h2>
          <p className="text-white/75 mb-8 text-lg leading-relaxed">{t("ds.form.subtitle")}</p>
          <ul className="space-y-3">
            {[1, 2, 3].map((i) => (
              <li key={i} className="flex items-start gap-3 text-white/85">
                <Check className="w-5 h-5 text-[#A6E5C8] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span>{t(`ds.form.b${i}`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6">
          <div className="bg-white rounded-3xl p-7 sm:p-10 text-[#0B1B2B] shadow-xl">
            {!done ? (
              <form onSubmit={submit} data-testid="sun-check-form" className="space-y-4">
                {referrerCode && (
                  <div data-testid="referral-banner" className="bg-[#F5C24C]/10 border border-[#F5C24C]/30 rounded-xl px-4 py-3 flex items-center gap-3">
                    <Sparkles className="w-4 h-4 text-[#E5AE2F]" />
                    <div className="text-sm">
                      <div className="font-medium">{t("landing.referral_active_title")}</div>
                      <div className="text-[#52606E] text-xs">{t("landing.referral_active_desc", { code: referrerCode })}</div>
                    </div>
                  </div>
                )}
                <Field data-testid="form-name" label={t("ds.form.name")} value={name} onChange={setName} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field data-testid="form-email" type="email" label={t("ds.form.email")} value={email} onChange={setEmail} />
                  <Field data-testid="form-whatsapp" label={t("ds.form.whatsapp")} value={whatsapp} onChange={setWhatsapp} placeholder="+34..." />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-medium">{t("ds.form.region")}</label>
                  <select data-testid="form-region" value={region} onChange={(e) => setRegion(e.target.value)}
                    className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-xl px-4 py-3 outline-none focus:border-[#F5C24C] focus:ring-2 focus:ring-[#F5C24C]/30 transition">
                    {regions.length === 0
                      ? <option value="madrid">Madrid</option>
                      : regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <Field data-testid="form-bill" type="number" label={t("ds.form.bill")} value={bill} onChange={setBill} suffix="€" />
                <button data-testid="form-submit" type="submit" disabled={busy}
                  className="w-full bg-[#0B1B2B] hover:bg-[#1F3A60] text-white rounded-full px-8 py-4 font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("ds.form.cta")} <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-[#52606E] text-center pt-2">{t("ds.form.privacy")}</p>
              </form>
            ) : (
              <div data-testid="form-success" className="text-center py-5">
                <div className="w-16 h-16 rounded-full bg-[#A6E5C8]/30 mx-auto mb-5 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-[#1F7A4B]" />
                </div>
                <h3 className="font-serif text-2xl mb-3">{t("ds.form.success_title")}</h3>
                <p className="text-[#52606E] mb-7">{t("ds.form.success_desc")}</p>
                <Link to={`/portal?token=${done.magic_token}`} className="inline-flex items-center gap-2 text-[#1F3A60] font-medium">
                  {t("ds.form.success_link")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, suffix, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-medium">{label}</label>
      <div className="relative">
        <input {...rest} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-xl px-4 py-3 outline-none focus:border-[#F5C24C] focus:ring-2 focus:ring-[#F5C24C]/30 transition" />
        {suffix && <span className="absolute right-4 top-3 text-sm text-[#52606E]">{suffix}</span>}
      </div>
    </div>
  );
}
